import React, { ReactElement, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { PrimaryButton } from '../../../../components/Buttons/PrimaryButton';
import { FormUnsavedChangesPrompt } from '../../../../components/FormUnsavedChangesPrompt';
import { HelpButton } from '../../../../components/HelpButton';
import { PageHeading } from '../../../../components/PageHeading';
import { REMOTE_ACCESS_HELP_LINKS } from '../../../../constants/user-guide';
import { useLeaveFormPrompt } from '../../hooks/useLeaveFormPrompt';
import { remoteAccessFieldsSchema } from '../../../../validation';
import { RemoteAccessProps } from './container';
import { RemoteAccessForm } from './RemoteAccessForm';
import {
  buildDhcpOptions,
  determineDhcpType,
  determineDhcpValue,
  determineIsDhcpOption43Enabled,
} from './utils/utils.ts';

export function RemoteAccess({
  data,
  isError,
  isFetching,
  isUpdating,
  updateRemoteAccess,
}: Required<RemoteAccessProps>): ReactElement {
  const { t } = useTranslation();

  const formMethods = useForm<RemoteAccessFormData>({
    defaultValues: {
      anyconnectEnabled: data.anyconnectEnabled,
      dhcpOption43Enabled: determineIsDhcpOption43Enabled(data),
      dhcpType: determineDhcpType(data),
      dhcpValue: determineDhcpValue(data),
      endpointKitEnabled: data.endpointKitEnabled,
    },
    mode: 'all',
    resolver: yupResolver(remoteAccessFieldsSchema),
  });

  const handleUpdateClicked: SubmitHandler<RemoteAccessFormData> = async (
    formData,
  ) => {
    const watchDhcpType = watch('dhcpType');
    const watchDhcpValue = watch('dhcpValue');

    await updateRemoteAccess({
      anyconnectEnabled: formData.anyconnectEnabled,
      dhcpOptions: buildDhcpOptions({
        dhcpOption43Enabled: formData.dhcpOption43Enabled,
        dhcpType: watchDhcpType,
        dhcpValue: watchDhcpValue,
      }),
      endpointKitEnabled: formData.endpointKitEnabled,
      topology: data.topology,
      uid: data.uid,
    });
    formMethods.reset(formData);
  };

  useEffect(() => {
    formMethods.reset({
      anyconnectEnabled: data.anyconnectEnabled,
      dhcpOption43Enabled: data.dhcpOptions?.length === 1,
      dhcpType: determineDhcpType(data),
      dhcpValue: determineDhcpValue(data),
      endpointKitEnabled: data.endpointKitEnabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { dirtyFields, isValid } = formMethods.formState;
  const isDirty = Object.keys(dirtyFields).length > 0;
  const { showModal, handleConfirm, handleCancel } =
    useLeaveFormPrompt(isDirty);
  const { control, watch, errors, trigger } = formMethods;
  const watchEndpointKitEnabled = watch('endpointKitEnabled');
  const watchDhcpOption43Enabled = watch('dhcpOption43Enabled');

  return (
    <section className="half-padding-left">
      <FormProvider {...formMethods}>
        <PageHeading
          ctaBtn={
            <PrimaryButton
              onClick={formMethods.handleSubmit(handleUpdateClicked)}
              testId="remote-access-form-submit-btn"
              type="submit"
              colour="success"
              disabled={!isDirty || !isValid}
              loading={isUpdating}
            >
              {t('buttons.update')}
            </PrimaryButton>
          }
          pageTitle={t('remoteAccess.name')}
        />
        {!isError && (
          <RemoteAccessForm
            control={control}
            watch={watch}
            errors={errors}
            trigger={trigger}
            isEndpointKitEnabled={watchEndpointKitEnabled}
            isDhcpOption43Enabled={watchDhcpOption43Enabled}
            isFetching={isFetching}
          />
        )}
      </FormProvider>
      <FormUnsavedChangesPrompt
        showModal={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
      <HelpButton links={REMOTE_ACCESS_HELP_LINKS} />
    </section>
  );
}
